import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import {
	MdOutlineChatBubbleOutline as ChatBubbleLeftIcon,
	MdOutlineAdd as PlusIcon,
	MdOutlinePerson as UserIcon,
	MdOutlineBuild as WrenchIcon,
	MdNotifications as NotificationsIcon,
	MdOutlineShare as ShareIcon,
} from "react-icons/md";
import { generateFallbackAvatar } from "../src/utils";
import { useNotifications } from "../src/hooks/useNotifications";
import ClickableAvatar from "./ClickableAvatar";
import ResilientImage from "./ResilientImage";
import type { SystemNotification } from "../types";

const Header: React.FC = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
	const { user, loading, isAdmin } = useAuth();
	const location = useLocation();
	const isHomePage = location.pathname === "/";
	const {
		notifications,
		unreadCount,
		markAsRead: handleMarkAsRead,
		dismiss: handleDismiss,
		markAllAsRead: handleMarkAllAsRead,
	} = useNotifications();

	// Close notifications dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				isNotificationsOpen &&
				!(event.target as Element).closest(".notifications-container")
			) {
				setIsNotificationsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isNotificationsOpen]);

	const handleLogout = async () => {
		try {
			await signOut(auth);
			setIsMenuOpen(false);
		} catch (error) {
			console.error("Error signing out: ", error);
		}
	};

	const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
		`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
			isActive
				? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg"
				: "text-slate-300 hover:text-white hover:bg-slate-700/50"
		}`;

	const mobileNavLinkClasses = (isActive: boolean) =>
		`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
			isActive
				? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
				: "text-slate-300 hover:text-white hover:bg-slate-700/50"
		}`;

	const AuthLinks = () => (
		<>
			{user ? (
				<div className="flex items-center gap-4">
					<Link
						to="/submit"
						className="hidden md:inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
					>
						<PlusIcon className="w-4 h-4 me-2" />
						הוסף מדריך
					</Link>
					{isAdmin && (
						<Link
							to="/admin"
							className="hidden md:inline-flex items-center px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
						>
							אדמין
						</Link>
					)}
					{/* Notifications Bell */}
					<div className="relative group notifications-container">
						<button
							onClick={(e) => {
								e.stopPropagation();
								setIsNotificationsOpen(!isNotificationsOpen);
							}}
							className="relative p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all duration-200"
						>
							<NotificationsIcon className="w-5 h-5" />
							{unreadCount > 0 && (
								<div
									className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-slate-800 flex items-center justify-center text-xs font-bold transition-colors duration-200 ${
										unreadCount > 0
											? "bg-red-500 text-white"
											: "bg-green-500 text-white"
									}`}
								>
									{unreadCount > 99 ? "99+" : unreadCount}
								</div>
							)}
						</button>

						{/* Notifications Dropdown */}
						{isNotificationsOpen && (
							<div className="absolute top-full right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-slate-800 rounded-lg shadow-2xl border border-slate-600/50 backdrop-blur-sm z-[9999] notifications-container">
								<div className="p-4 border-b border-slate-700">
									<div className="flex items-center justify-between">
										<h3 className="text-white font-semibold flex items-center gap-2">
											<NotificationsIcon className="w-4 h-4" />
											התראות
										</h3>
										{unreadCount > 0 && (
											<button
												onClick={handleMarkAllAsRead}
												className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
											>
												סמן הכל כנקרא
											</button>
										)}
									</div>
								</div>

								<div className="max-h-96 overflow-y-auto">
									{notifications.length === 0 ? (
										<div className="p-8 text-center">
											<NotificationsIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
											<p className="text-slate-400 text-sm">אין התראות חדשות</p>
										</div>
									) : (
										<div className="p-2">
											{notifications.slice(0, 10).map((notification) => (
												<div
													key={notification.id}
													className={`p-3 rounded-lg mb-2 border transition-all duration-200 ${
														!notification.isRead
															? "bg-slate-700/50 border-slate-600 shadow-sm"
															: "bg-slate-800/30 border-slate-700/50"
													}`}
												>
													<div className="flex items-start gap-3">
														<div className="flex-1 min-w-0">
															<h4 className="text-white text-sm font-medium mb-1 truncate">
																{notification.title}
															</h4>
															<p className="text-slate-300 text-xs mb-2 line-clamp-2">
																{notification.message}
															</p>
															<p className="text-slate-400 text-xs">
																{notification.createdAt.toLocaleString("he-IL")}
															</p>
														</div>
														<div className="flex flex-col gap-1">
															{!notification.isRead && (
																<button
																	onClick={() =>
																		handleMarkAsRead(notification.id)
																	}
																	className="text-xs text-cyan-400 hover:text-cyan-300 p-1"
																	title="סמן כנקרא"
																>
																	✓
																</button>
															)}
															<button
																onClick={() => handleDismiss(notification.id)}
																className="text-xs text-red-400 hover:text-red-300 p-1"
																title="הסר התראה"
															>
																×
															</button>
														</div>
													</div>
												</div>
											))}
											{notifications.length > 10 && (
												<div className="p-2 text-center">
													<Link
														to="/notifications"
														className="text-xs text-cyan-400 hover:text-cyan-300"
														onClick={() => setIsNotificationsOpen(false)}
													>
														ראה את כל {notifications.length} ההתראות
													</Link>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Profile */}
					<div className="relative group">
						<div className="cursor-pointer">
							<div className="relative">
								<ResilientImage
									src={
										user.photoURL ||
										generateFallbackAvatar(user.displayName || "User")
									}
									alt={user.displayName || "User"}
									className="h-9 w-9 rounded-full ring-2 ring-cyan-400/50 hover:ring-cyan-400 transition-all duration-200"
									priority={true}
									retryAttempts={5}
									retryDelay={500}
									fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(
										user.displayName || "User"
									)}&background=0284c7&color=fff`}
								/>
								<div
									className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-800 ${
										unreadCount > 0 ? "bg-red-500" : "bg-green-500"
									}`}
								></div>
							</div>
						</div>
						<div className="absolute top-full left-0 mt-2 w-56 max-w-[90vw] bg-slate-700 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999] border border-slate-600/50 backdrop-blur-sm">
							<div className="py-2">
								<Link
									to="/profile"
									className="flex items-center space-x-3 px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-600 transition-colors"
								>
									<UserIcon className="w-4 h-4" />
									<span>הפרופיל שלי</span>
								</Link>
								<button
									onClick={handleLogout}
									className="flex items-center space-x-3 w-full px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-600 transition-colors text-left"
								>
									<svg
										className="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
										/>
									</svg>
									<span>יציאה</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="flex items-center space-x-3">
					<Link
						to="/login"
						className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
					>
						התחברות
					</Link>
				</div>
			)}
		</>
	);

	return (
		<header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-xl sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-sm overflow-visible">
			<div className="container mx-auto px-4 relative overflow-visible">
				<div className="flex items-center justify-between h-16">
					{/* Left side: Logo and Nav */}
					<div className="flex items-center space-x-8">
						<Link to="/" className="flex items-center space-x-3 group">
							<div className="relative">
								<WrenchIcon className="w-8 h-8 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-200" />
								<div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
							</div>
							<span className="text-white text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
								תיקנתי
							</span>
						</Link>
						<nav className="hidden lg:flex items-center space-x-2">
							<NavLink to="/guides" className={navLinkClasses}>
								מדריכים
							</NavLink>
							<NavLink to="/forum" className={navLinkClasses}>
								פורום
							</NavLink>
							<NavLink to="/sharing" className={navLinkClasses}>
								שיתוף
							</NavLink>
						</nav>
					</div>

					{/* Right side: Auth and Hamburger */}
					<div className="flex items-center space-x-6">
						<div className="hidden md:block">{!loading && <AuthLinks />}</div>
						<div className="lg:hidden">
							<button
								onClick={() => setIsMenuOpen(!isMenuOpen)}
								className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all duration-200"
							>
								<span className="sr-only">Open main menu</span>
								<div className="w-6 h-6 flex flex-col justify-center items-center">
									<span
										className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
											isMenuOpen ? "rotate-45 translate-y-1" : "-translate-y-1"
										}`}
									></span>
									<span
										className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
											isMenuOpen ? "opacity-0" : "opacity-100"
										}`}
									></span>
									<span
										className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
											isMenuOpen ? "-rotate-45 -translate-y-1" : "translate-y-1"
										}`}
									></span>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile Menu */}
			<div
				className={`fixed lg:hidden top-16 left-0 w-80 h-[calc(100vh-4rem)] transition-all duration-300 ease-in-out z-50 ${
					isMenuOpen
						? "opacity-100 translate-x-0"
						: "opacity-0 -translate-x-full"
				}`}
			>
				<div className="h-full px-4 py-4 space-y-2 bg-slate-800/95 backdrop-blur-sm border-r border-slate-700/50 overflow-y-auto">
					{/* Profile Section for Mobile - Top of Menu */}
					<div className="border-b border-slate-700/50 pb-4 mb-4">
						{!loading && user ? (
							<div className="space-y-3">
								<div className="flex items-center justify-between w-full px-4 py-3">
									<div className="flex items-center space-x-3 flex-1 min-w-0">
										<div className="flex items-center space-x-3 text-left flex-1 min-w-0">
											<div className="relative">
												<ResilientImage
													src={
														user.photoURL ||
														`https://ui-avatars.com/api/?name=${user.displayName}&background=0284c7&color=fff`
													}
													alt=""
													className="h-10 w-10 rounded-full ring-2 ring-cyan-400/50 hover:ring-cyan-400 transition-all duration-200"
													priority={true}
													retryAttempts={5}
													retryDelay={500}
												/>
												<div
													className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-slate-800 flex items-center justify-center text-xs font-bold transition-all duration-200 ${
														unreadCount > 0
															? "bg-red-500 text-white"
															: "bg-green-500 text-white"
													}`}
												>
													{unreadCount > 99 ? "99+" : unreadCount}
												</div>
											</div>
											<div>
												<div className="text-white font-medium text-sm">
													{user.displayName}
												</div>
												<div className="text-slate-400 text-xs">
													{user.email}
												</div>
											</div>
										</div>
									</div>
									<div className="flex items-center flex-shrink-0">
										<button
											onClick={handleLogout}
											className="flex items-center space-x-2 text-slate-300 hover:text-white hover:bg-red-500/20 rounded-lg px-3 py-2 transition-all duration-200"
											title="יציאה"
										>
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
												/>
											</svg>
											<span className="text-sm font-medium">יציאה</span>
										</button>
									</div>
								</div>
							</div>
						) : (
							<NavLink
								to="/login"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<UserIcon className="w-5 h-5" />
								<span>התחברות</span>
							</NavLink>
						)}
					</div>

					<NavLink
						to="/guides"
						className={({ isActive }) => mobileNavLinkClasses(isActive)}
						onClick={() => setIsMenuOpen(false)}
					>
						<WrenchIcon className="w-5 h-5" />
						<span>מדריכים</span>
					</NavLink>
					<NavLink
						to="/forum"
						className={({ isActive }) => mobileNavLinkClasses(isActive)}
						onClick={() => setIsMenuOpen(false)}
					>
						<ChatBubbleLeftIcon className="w-5 h-5" />
						<span>פורום</span>
					</NavLink>
					<NavLink
						to="/sharing"
						className={({ isActive }) => mobileNavLinkClasses(isActive)}
						onClick={() => setIsMenuOpen(false)}
					>
						<ShareIcon className="w-5 h-5" />
						<span>שיתוף</span>
					</NavLink>

					{!loading && user && (
						<NavLink
							to="/submit"
							className={({ isActive }) => mobileNavLinkClasses(isActive)}
							onClick={() => setIsMenuOpen(false)}
						>
							<PlusIcon className="w-5 h-5" />
							<span>הוסף מדריך</span>
						</NavLink>
					)}

					{/* Mobile Notifications Dropdown */}
					{isNotificationsOpen && (
						<div className="fixed lg:hidden top-16 right-4 w-72 max-w-[calc(100vw-2rem)] bg-slate-800 rounded-lg shadow-2xl border border-slate-600/50 backdrop-blur-sm z-[9999] notifications-container">
							<div className="p-4 border-b border-slate-700">
								<div className="flex items-center justify-between">
									<h3 className="text-white font-semibold flex items-center gap-2">
										<NotificationsIcon className="w-4 h-4" />
										התראות
									</h3>
									{unreadCount > 0 && (
										<button
											onClick={handleMarkAllAsRead}
											className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
										>
											סמן הכל כנקרא
										</button>
									)}
								</div>
							</div>

							<div className="max-h-96 overflow-y-auto">
								{notifications.length === 0 ? (
									<div className="p-8 text-center">
										<NotificationsIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
										<p className="text-slate-400 text-sm">אין התראות חדשות</p>
									</div>
								) : (
									<div className="p-2">
										{notifications.slice(0, 10).map((notification) => (
											<div
												key={notification.id}
												className={`p-3 rounded-lg mb-2 border transition-all duration-200 ${
													!notification.isRead
														? "bg-slate-700/50 border-slate-600 shadow-sm"
														: "bg-slate-800/30 border-slate-700/50"
												}`}
											>
												<div className="flex items-start gap-3">
													<div className="flex-1 min-w-0">
														<h4 className="text-white text-sm font-medium mb-1 truncate">
															{notification.title}
														</h4>
														<p className="text-slate-300 text-xs mb-2 line-clamp-2">
															{notification.message}
														</p>
														<p className="text-slate-400 text-xs">
															{notification.createdAt.toLocaleString("he-IL")}
														</p>
													</div>
													<div className="flex flex-col gap-1">
														{!notification.isRead && (
															<button
																onClick={() =>
																	handleMarkAsRead(notification.id)
																}
																className="text-xs text-cyan-400 hover:text-cyan-300 p-1"
																title="סמן כנקרא"
															>
																✓
															</button>
														)}
														<button
															onClick={() => handleDismiss(notification.id)}
															className="text-xs text-red-400 hover:text-red-300 p-1"
															title="הסר התראה"
														>
															×
														</button>
													</div>
												</div>
											</div>
										))}
										{notifications.length > 10 && (
											<div className="p-2 text-center">
												<Link
													to="/notifications"
													className="text-xs text-cyan-400 hover:text-cyan-300"
													onClick={() => {
														setIsNotificationsOpen(false);
														setIsMenuOpen(false);
													}}
												>
													ראה את כל {notifications.length} ההתראות
												</Link>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Footer Links Section */}
					<div className="border-t border-slate-700/50 pt-4 mt-4">
						<div className="space-y-2">
							<h4 className="text-slate-400 text-sm font-medium px-4 mb-3">
								הקהילה
							</h4>
							<NavLink
								to="/join"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<UserIcon className="w-5 h-5" />
								<span>הצטרף אלינו</span>
							</NavLink>
							<NavLink
								to="/writing-guide"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<WrenchIcon className="w-5 h-5" />
								<span>מדריכי כתיבה</span>
							</NavLink>
							<NavLink
								to="/rewards"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
								</svg>
								<span>תגמולים</span>
							</NavLink>
							<NavLink
								to="/leaderboard"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M7.5 5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM9 12h6v-2H9v2zm0 4h6v-2H9v2zM3 3v18h18V3H3zm16 16H5V5h14v14z" />
								</svg>
								<span>לוח תוצאות</span>
							</NavLink>
						</div>

						<div className="space-y-2 mt-6">
							<h4 className="text-slate-400 text-sm font-medium px-4 mb-3">
								תמיכה
							</h4>
							<NavLink
								to="/contact"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
								</svg>
								<span>צור קשר</span>
							</NavLink>
							<NavLink
								to="/bug-report"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
								</svg>
								<span>דיווח על באג</span>
							</NavLink>
							<NavLink
								to="/faq"
								className={({ isActive }) => mobileNavLinkClasses(isActive)}
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 16h2v-2h-2v2zm0-4h2V8h-2v6z" />
								</svg>
								<span>שאלות נפוצות</span>
							</NavLink>
						</div>

						{/* Buy Me a Coffee Link */}
						<div className="border-t border-slate-700/50 pt-4 mt-6">
							<a
								href="https://buymeacoffee.com/nir.hochman"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 text-slate-300 hover:text-white hover:bg-cyan-500/20"
								onClick={() => setIsMenuOpen(false)}
							>
								<svg
									className="w-5 h-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M18.5 3H6c-1.1 0-2 .9-2 2v5.71c0 3.83 2.95 7.18 6.78 7.29 3.96.12 7.22-3.06 7.22-7v-1h.5c1.38 0 2.5-1.12 2.5-2.5S19.88 3 18.5 3zM16 5v5.61c0 2.96-2.3 5.47-5.27 5.38-2.78-.08-4.73-2.35-4.73-5.08V5h10zM20 8h-2V5c0-.55-.45-1-1-1s-1 .45-1 1v3h-2c-.55 0-1 .45-1 1s.45 1 1 1h6c.55 0 1-.45 1-1s-.45-1-1-1z" />
								</svg>
								<span>Buy me a coffee</span>
							</a>
						</div>
					</div>
				</div>
			</div>
		</header>
	);
};

export default Header;
